/**
 * TeacherUnifiedLesson — teacher-side view of today's cross-grade unified
 * lesson (grades 5-8). Shows the whole-group teacher script, then a card
 * per grade with concept + embedded YouTube videos + practice + exit ticket.
 *
 * Fetches from GET /api/school/today/unified-lesson (teacher token → full plan).
 * Regenerate button hits POST /api/school/admin/unified-lesson/regenerate.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { BookOpen, Loader2, RefreshCw, Sparkles, Youtube, ChevronLeft, ChevronRight, Calendar, Zap, FileDown } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const GRADE_ACCENTS = {
  5: 'border-cyan-500/60 bg-cyan-500/5 text-cyan-300',
  6: 'border-emerald-500/60 bg-emerald-500/5 text-emerald-300',
  7: 'border-orange-500/60 bg-orange-500/5 text-orange-300',
  8: 'border-fuchsia-500/60 bg-fuchsia-500/5 text-fuchsia-300',
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const nextSchoolDay = (iso, direction = 1) => {
  const d = new Date(iso + 'T12:00:00');
  do {
    d.setDate(d.getDate() + direction);
  } while (d.getDay() === 0 || d.getDay() === 6);
  return d.toISOString().slice(0, 10);
};

const prettyDate = (iso) => {
  try {
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  } catch { return iso; }
};

const TeacherUnifiedLesson = () => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [prewarming, setPrewarming] = useState(false);
  const [dateIso, setDateIso] = useState(todayIso());

  const load = useCallback((iso) => {
    setLoading(true);
    api.get(`/school/today/unified-lesson?view=teacher&date=${iso}`, { headers: authHeaders() })
      .then((r) => setPlan(r.data?.plan || null))
      .catch(() => setPlan(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(dateIso); }, [dateIso, load]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const r = await api.post('/school/admin/unified-lesson/regenerate', { date: dateIso }, { headers: authHeaders(), timeout: 90000 });
      setPlan(r.data?.plan || null);
      toast.success(`${prettyDate(dateIso)} refreshed with fresh videos.`);
    } catch (e) {
      toast.error("Could not regenerate — try again in a moment.");
    } finally {
      setRegenerating(false);
    }
  };

  const prewarm = async () => {
    setPrewarming(true);
    try {
      const r = await api.post('/school/admin/unified-lesson/prewarm', { days: 10, start_date: dateIso }, {
        headers: authHeaders(),
        timeout: 30000,
      });
      const msg = r.data?.message || `Prewarm queued for ${r.data?.requested_days || 10} school days.`;
      toast.success(msg);
    } catch (e) {
      toast.error("Could not queue prewarm — try again in a moment.");
    } finally {
      setPrewarming(false);
    }
  };

  if (loading) {
    return (
      <div className="border-2 border-zinc-800 px-5 py-4 flex items-center gap-3" data-testid="teacher-unified-loading">
        <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
        <span className="text-xs text-zinc-500">Loading unified lesson for {prettyDate(dateIso)}…</span>
      </div>
    );
  }

  const hasPlan = !!plan?.grade_progression?.length;
  const isToday = dateIso === todayIso();

  return (
    <div className="border-2 border-emerald-500/50 bg-black" data-testid="teacher-unified-lesson">
      {/* Date navigation bar */}
      <div className="border-b border-zinc-900 px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDateIso(nextSchoolDay(dateIso, -1))}
            className="p-2 border-2 border-zinc-700 hover:border-emerald-500 hover:text-emerald-300 text-zinc-400 transition-colors"
            data-testid="teacher-unified-prev-day"
            title="Previous school day"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 border-2 border-zinc-700 px-3 py-1.5">
            <Calendar className="w-3.5 h-3.5 text-emerald-400" />
            <input
              type="date"
              value={dateIso}
              onChange={(e) => setDateIso(e.target.value)}
              className="bg-transparent text-white text-xs font-mono focus:outline-none"
              data-testid="teacher-unified-date-input"
            />
            {!isToday && (
              <button
                onClick={() => setDateIso(todayIso())}
                className="text-[9px] uppercase tracking-widest font-black text-emerald-400 hover:text-emerald-300 pl-2 border-l border-zinc-800"
                data-testid="teacher-unified-jump-today"
              >
                Today
              </button>
            )}
          </div>
          <button
            onClick={() => setDateIso(nextSchoolDay(dateIso, 1))}
            className="p-2 border-2 border-zinc-700 hover:border-emerald-500 hover:text-emerald-300 text-zinc-400 transition-colors"
            data-testid="teacher-unified-next-day"
            title="Next school day"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={prewarm}
            disabled={prewarming}
            className="inline-flex items-center gap-1.5 border-2 border-orange-500/70 hover:bg-orange-500 hover:text-black text-orange-300 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors disabled:opacity-50"
            data-testid="teacher-unified-prewarm"
            title="Pre-generate the next 10 school days so you can plan supplies ahead"
          >
            {prewarming ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
            Look Ahead · 10 Days
          </button>
          {hasPlan && (
            <button
              onClick={regenerate}
              disabled={regenerating}
              className="inline-flex items-center gap-1.5 border-2 border-emerald-500/60 hover:bg-emerald-500 hover:text-black text-emerald-300 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors disabled:opacity-50"
              data-testid="teacher-unified-regenerate"
            >
              {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Regen
            </button>
          )}
          {hasPlan && (
            <a
              href={`${API_BASE}/api/school/teacher/playbook-deck?date=${dateIso}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                // Inject the auth header via fetch so the PDF can auth-download.
                // We fetch → blob → object-URL → open, since <a> can't send headers.
                e.preventDefault();
                (async () => {
                  try {
                    const r = await fetch(`${API_BASE}/api/school/teacher/playbook-deck?date=${dateIso}`, {
                      headers: authHeaders(),
                    });
                    if (!r.ok) throw new Error(`HTTP ${r.status}`);
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank', 'noopener,noreferrer');
                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                  } catch (err) {
                    toast.error('Could not open playbook deck — try Regen first.');
                  }
                })();
              }}
              className="inline-flex items-center gap-1.5 border-2 border-orange-500/60 hover:bg-orange-500 hover:text-black text-orange-300 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors"
              data-testid="teacher-unified-deck-download"
              title="Download today's lesson as a sports-themed teacher playbook PDF"
            >
              <FileDown className="w-3 h-3" /> Playbook PDF
            </a>
          )}
        </div>
      </div>

      {/* If the picked day isn't a school day OR isn't yet built, show a build prompt */}
      {!hasPlan && (
        <div className="px-5 py-8 flex flex-col items-center justify-center text-center gap-3" data-testid="teacher-unified-empty">
          <div className="w-12 h-12 border-2 border-emerald-500/40 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="text-white font-black text-base">No unified plan for {prettyDate(dateIso)} yet</div>
          <p className="text-xs text-zinc-500 max-w-md">
            Weekends aren't scheduled. For a school day, tap <span className="text-orange-300 font-black">Look Ahead</span> above
            to prewarm the next 10 days at once, or use <span className="text-emerald-300 font-black">Regen</span> after picking a valid date.
          </p>
        </div>
      )}

      {hasPlan && (
      <>
      <div className="border-b border-zinc-900 px-5 py-4 flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-5 h-5 text-black" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] font-black text-emerald-400">Unified Lesson · Grades 5-8</div>
          <div className="text-white font-black text-base leading-tight break-words">{plan.topic || plan.subject}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{prettyDate(plan.date || dateIso)} · {plan.subject}</div>
        </div>
      </div>

      <div className="px-5 py-5 space-y-5">
        {/* Whole-group teacher script */}
        {plan.unified_teacher_script && (
          <div className="border-2 border-orange-500/40 bg-orange-500/5 px-4 py-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-3 h-3 text-orange-300" />
              <span className="text-[9px] uppercase tracking-widest font-black text-orange-300">Whole-Group Opening Script</span>
            </div>
            <p className="text-[13px] text-zinc-100 leading-relaxed whitespace-pre-wrap">
              {plan.unified_teacher_script}
            </p>
          </div>
        )}

        {/* Per-grade cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {plan.grade_progression.map((gp) => {
            const accent = GRADE_ACCENTS[gp.grade] || 'border-zinc-700 bg-zinc-900/40 text-zinc-300';
            return (
              <div key={gp.grade} className={`border-2 ${accent.split(' ').slice(0, 2).join(' ')} p-4`} data-testid={`teacher-unified-grade-${gp.grade}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className={`text-[10px] uppercase tracking-widest font-black ${accent.split(' ').slice(2).join(' ')}`}>
                    Grade {gp.grade}
                  </div>
                  {gp.standard_code && (
                    <span className="text-[10px] font-mono text-zinc-400">{gp.standard_code}</span>
                  )}
                </div>
                {gp.concept && (
                  <p className="text-[13px] text-zinc-200 leading-snug mb-3">{gp.concept}</p>
                )}

                {(gp.videos || []).length > 0 && (
                  <div className="mb-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Youtube className="w-3 h-3 text-red-400" />
                      <span className="text-[9px] uppercase tracking-widest font-black text-zinc-400">Videos</span>
                    </div>
                    <div className="space-y-2">
                      {gp.videos.map((v, i) => (
                        <div key={i} className="border border-zinc-800 bg-black overflow-hidden" data-testid={`teacher-unified-grade-${gp.grade}-video-${i}`}>
                          <div className="relative pb-[56.25%] h-0 bg-black">
                            <iframe
                              src={v.embed_url}
                              title={v.title}
                              className="absolute inset-0 w-full h-full"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                          <div className="px-2 py-1.5">
                            <div className="text-[11px] font-bold text-white leading-tight line-clamp-2">{v.title}</div>
                            <div className="text-[9px] text-zinc-500 mt-0.5">{v.channel}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(gp.practice_problems || []).length > 0 && (
                  <div className="mb-3">
                    <div className="text-[9px] uppercase tracking-widest font-black text-zinc-400 mb-1.5">Practice</div>
                    <ol className="space-y-1 pl-1">
                      {gp.practice_problems.map((p, i) => (
                        <li key={i} className="flex gap-1.5 text-[12px] text-zinc-300 leading-snug">
                          <span className="flex-shrink-0 text-zinc-500 font-mono">{i + 1}.</span>
                          <span className="min-w-0">{p}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {plan.exit_tickets?.[String(gp.grade)] && (
                  <div className="border-t border-zinc-800 pt-2">
                    <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500 mb-1">Exit Ticket</div>
                    <p className="text-[12px] text-zinc-200 leading-snug italic">"{plan.exit_tickets[String(gp.grade)]}"</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default TeacherUnifiedLesson;
